import { Item } from '@/api/items/items.types';
import { theme } from '@/config/theme';
import { RenderCartQuantity } from '@components/render-cart-quantity.component';
import { Box, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { CellBoldMoneyRenderer } from '@utils/cell-bold.renderer';
import { MoneyValueZeroOnRequestProps } from '@utils/money-value-formatter';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const STOCK_COLORS = {
  available: '#43a047',
  ordered: '#ef6c00',
  unavailable: '#e53935',
} as const;

function getStockInfo(
  row: Item,
  t: (key: string, opts?: Record<string, unknown>) => string,
) {
  const available = Math.max(
    row.quantityOnStock - row.quantityOrderedByCustomers,
    0,
  );
  const ordered = row.quantityOrderedFromVendors;

  if (available === 0 && ordered === 0) {
    return {
      color: STOCK_COLORS.unavailable,
      label: t('items.stock.notAvailable'),
      tooltip: t('items.stock.notAvailableTooltip'),
    };
  }
  if (available === 0 && ordered > 0) {
    return {
      color: STOCK_COLORS.ordered,
      label: t('items.stock.ordered'),
      tooltip: t('items.stock.deliveryDate'),
    };
  }

  const label =
    available < 20
      ? `${available}`
      : available < 50
        ? '> 20'
        : available < 100
          ? '> 50'
          : available < 200
            ? '> 100'
            : available < 500
              ? '> 200'
              : '> 500';

  return {
    color: STOCK_COLORS.available,
    label,
    tooltip:
      ordered > 0
        ? `${label}, ${t('items.stock.additionalOrdered', { count: ordered, date: 'N/A' })}`
        : label,
  };
}

export function useItemsGridColumns(
  itemGroups: string[],
  manufacturers: string[],
) {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation('customer');

  return useMemo<GridColDef<Item>[]>(
    () => [
      {
        field: 'id',
        headerName: `${t('items.columns.itemNumber')} / ${t('items.columns.ean')}`,
        width: 170,
        renderCell: (params) => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '100%',
              overflow: 'hidden',
              lineHeight: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Roboto Mono", monospace',
                fontSize: '0.78rem',
                fontWeight: 500,
                letterSpacing: '0.02em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {params.row.id}
            </Typography>
            {params.row.barCode && (
              <Typography
                sx={{
                  fontFamily: '"Roboto Mono", monospace',
                  fontSize: '0.65rem',
                  color: 'text.disabled',
                  letterSpacing: '0.02em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  mt: '1px',
                }}
              >
                {params.row.barCode}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'name',
        headerName: t('items.columns.description'),
        flex: 1,
        minWidth: 350,
        renderCell: (params) => {
          const stock = getStockInfo(params.row, t);
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                gap: 1.5,
                overflow: 'hidden',
              }}
            >
              {params.row.imageUrl ? (
                <img
                  src={params.row.imageUrl}
                  alt=""
                  loading="lazy"
                  style={{
                    width: 28,
                    height: 28,
                    objectFit: 'contain',
                    borderRadius: 4,
                    flexShrink: 0,
                    display: 'block',
                  }}
                />
              ) : (
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0 }}
                >
                  <rect width="36" height="36" rx="4" fill="#f5f5f5" />
                  <path
                    d="M12 22l3-4 2.25 3 3-4L24 22"
                    stroke="#bdbdbd"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <circle cx="15" cy="15" r="1.5" fill="#bdbdbd" />
                </svg>
              )}
              <Tooltip title={params.row.name} enterDelay={300} arrow>
                <Typography
                  sx={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flexGrow: 1,
                    minWidth: 0,
                  }}
                >
                  {params.row.name}
                </Typography>
              </Tooltip>
              <Tooltip title={stock.tooltip} arrow>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    flexShrink: 0,
                    ml: 'auto',
                    cursor: 'default',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                      color: 'text.secondary',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {stock.label}
                  </Typography>
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      bgcolor: stock.color,
                      flexShrink: 0,
                      boxShadow: `0 0 0 2px ${stock.color}22`,
                    }}
                  />
                </Box>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: 'itemGroup',
        headerName: t('items.columns.category'),
        type: 'singleSelect',
        width: 150,
        valueOptions: itemGroups,
        hideable: true,
      },
      {
        field: 'manufacturer',
        headerName: t('items.columns.manufacturer'),
        type: 'singleSelect',
        width: 150,
        valueOptions: manufacturers,
        hideable: true,
      },
      {
        field: 'itemPrice',
        headerName: t('items.columns.price'),
        ...MoneyValueZeroOnRequestProps,
        renderCell: CellBoldMoneyRenderer,
      },
      {
        field: 'cartQuantity',
        headerName: '',
        type: 'number',
        disableColumnMenu: true,
        disableReorder: true,
        disableExport: true,
        minWidth: isMobile ? 60 : 150,
        width: isMobile ? 60 : 150,
        renderCell: (props) => RenderCartQuantity({ ...props }),
      },
    ],
    [isMobile, itemGroups, manufacturers, t],
  );
}
